import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  MessageCircle, 
  Activity, 
  TrendingUp,
  Plus 
} from 'lucide-react';

const Dashboard = () => {
  const { data: clients, isLoading } = useQuery(
    'dashboard-clients',
    async () => {
      const response = await axios.get('/clients?limit=5');
      return response.data.clients;
    }
  );

  const stats = [
    {
      title: 'Total Clients',
      value: clients?.length || 0,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Active Chatbots',
      value: clients?.filter(c => c.chatbotConfig?.enabled).length || 0,
      icon: MessageCircle,
      color: 'bg-green-500'
    },
    {
      title: 'Total Queries',
      value: clients?.reduce((acc, c) => acc + (c.usage?.totalQueries || 0), 0) || 0,
      icon: Activity,
      color: 'bg-purple-500'
    },
    {
      title: 'Monthly Queries',
      value: clients?.reduce((acc, c) => acc + (c.usage?.monthlyQueries || 0), 0) || 0,
      icon: TrendingUp,
      color: 'bg-orange-500'
    }
  ];

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow h-24"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your chatbot platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Recent Clients</h2>
              <Link
                to="/clients"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {clients && clients.length > 0 ? (
              <div className="space-y-4">
                {clients.slice(0, 5).map((client) => (
                  <div key={client._id} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{client.name}</p>
                      <p className="text-sm text-gray-500">{client.website}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {client.usage?.totalQueries || 0} queries
                      </p>
                      <p className={`text-xs ${client.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {client.isActive ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No clients yet</p>
                <Link
                  to="/clients"
                  className="inline-flex items-center mt-2 px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Client
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              <Link
                to="/clients"
                className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Plus className="h-5 w-5 mr-3 text-gray-400" />
                Add New Client
              </Link>
              <Link
                to="/clients"
                className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Users className="h-5 w-5 mr-3 text-gray-400" />
                Manage Clients
              </Link>
              <Link
                to="/clients"
                className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <MessageCircle className="h-5 w-5 mr-3 text-gray-400" />
                View Analytics
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;